/**
 * File: confirm_dialog.tsx
 * Description: Reusable confirmation dialog component for the admin interface. Presents
 * a title, custom body content, and Cancel / Confirm action buttons. Handles the
 * submitting state by disabling both buttons and showing a spinner on the confirm button.
 */
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import type { 
  SxProps, 
  Theme
} from '@mui/material';

/**
 * Props for the ConfirmDialog component.
 *
 * @property {boolean} open - Whether the dialog is visible.
 * @property {string} title - Text displayed in the dialog title bar.
 * @property {() => void} onClose - Called when the user dismisses or cancels the dialog.
 * @property {() => void} onConfirm - Called when the user clicks the confirm button.
 * @property {boolean} isSubmitting - When true, both buttons are disabled and a spinner replaces the confirm label.
 * @property {string} [confirmLabel] - Label for the confirm button. Defaults to "Confirmar".
 * @property {'primary' | 'error'} [confirmColor] - MUI color applied to the confirm button. Defaults to "primary".
 * @property {SxProps<Theme>} [confirmSx] - Additional MUI sx styles applied to the confirm button. Overrides defaults.
 * @property {React.ReactNode} children - Custom body content rendered inside the dialog.
 */
interface ConfirmDialogProps {
  open: boolean;
  title: string;
  onClose: () => void;
  onConfirm: () => void;
  isSubmitting: boolean;
  confirmLabel?: string;
  confirmColor?: 'primary' | 'error';
  confirmSx?: SxProps<Theme>;
  children: React.ReactNode;
}

/**
 * Pure presentational dialog that asks the user to confirm or cancel an action.
 * Renders arbitrary body content via children, disables both action buttons while
 * a submission is in progress, and swaps the confirm label for a spinner.
 *
 * @param {boolean} props.open - Controls dialog visibility.
 * @param {string} props.title - Dialog title text.
 * @param {() => void} props.onClose - Handler for cancel / backdrop close.
 * @param {() => void} props.onConfirm - Handler for the confirm action.
 * @param {boolean} props.isSubmitting - Disables buttons and shows a loading spinner when true.
 * @param {string} [props.confirmLabel] - Confirm button label (default: "Confirmar").
 * @param {'primary' | 'error'} [props.confirmColor] - Confirm button MUI color (default: "primary").
 * @param {SxProps<Theme>} [props.confirmSx] - Additional MUI sx styles for the confirm button. Overrides defaults.
 * @param {React.ReactNode} props.children - Body content of the dialog.
 * @returns {JSX.Element}
 */
function ConfirmDialog({
  open,
  title,
  onClose,
  onConfirm,
  isSubmitting,
  confirmLabel = 'Confirmar',
  confirmColor = 'primary',
  confirmSx,
  children,
}: ConfirmDialogProps) {

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {<Typography variant='h5'>{title}</Typography>}
      </DialogTitle>
      <DialogContent>{children}</DialogContent>
      <DialogActions>
        <Button  variant="contained" onClick={onClose} disabled={isSubmitting}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          color={confirmColor}
          onClick={onConfirm}
          disabled={isSubmitting}
          sx={{
            ...confirmSx as object,
          }}
        >
          {isSubmitting ? <CircularProgress size={20} /> : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ConfirmDialog;
